import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useSWR from 'swr';

import { API } from 'constants';
import client from '../../../services/report-api';

import { STATUS } from '../../../constants/statuses';

import { useHandlePageDataLoadError } from '../../../hooks/useHandlePageDataLoadError';

import Loading from '../../../components/Loading';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import RenameProjectModal from '../RenameProjectModal/RenameProjectModal';
import ProjectDetailMainPanel from './ProjectDetailMainPanel/ProjectDetailMainPanel';
import ProjectDetailPageHeader from './ProjectDetailPageHeader/ProjectDetailPageHeader';
import ConfirmArchiveProjectModal from '../ConfirmArchiveProjectModal/ConfirmArchiveProjectModal';

const fetcher = (url) => client.get(url).then((res) => res.data);

const ProjectDetailPage = () => {
    const { projectId } = useParams();

    const navigate = useNavigate();
    const location = useLocation();
    const backLinkHref = location.state?.from ?? '/projects';

    const [shouldRefresh, setShouldRefresh] = useState(true); // if project.status === 'started' auto polling is enabled

    const requestEndpoint = `${API.ROUTES.report.project}${projectId}/`;
    const {
        data: projectDetail,
        error,
        mutate,
    } = useSWR(requestEndpoint, fetcher, {
        refreshInterval: shouldRefresh ? 1000 : 0,
    });

    const [actionModal, setActionModal] = useState({ action: null });

    const { handlePageDataLoadError } = useHandlePageDataLoadError();

    useEffect(() => {
        if (projectDetail) {
            setShouldRefresh(projectDetail.status === STATUS.started);
        }
    }, [projectDetail]);

    if (error) {
        handlePageDataLoadError({
            e: error,
            redirectPath: '/projects',
            generalErrorHandler: () => navigate(backLinkHref),
        });
    }

    useDocumentTitle(projectDetail?.name);

    return (
        <div className="page-position bg-white flex flex-col hide-scrollbar">
            {projectDetail && (
                <>
                    <ProjectDetailPageHeader
                        projectDetail={projectDetail}
                        backLinkHref={backLinkHref}
                        setActionModal={setActionModal}
                    />

                    <ProjectDetailMainPanel projectDetail={projectDetail} />

                    {actionModal.action === 'rename' && (
                        <RenameProjectModal
                            projectId={projectId}
                            projectName={projectDetail.name}
                            mutate={mutate}
                            onClose={() => setActionModal({ action: null })}
                        />
                    )}

                    {actionModal.action === 'archive' && (
                        <ConfirmArchiveProjectModal
                            projectId={projectId}
                            projectName={projectDetail.name}
                            backLinkHref={backLinkHref}
                            onClose={() => setActionModal({ action: null })}
                        />
                    )}
                </>
            )}

            {!projectDetail && (
                <div className="flex-grow flex justify-center">
                    <Loading />
                </div>
            )}
        </div>
    );
};

export default ProjectDetailPage;
